import { toast as sonnerToast } from 'sonner';
import { getConvexErrorMessage } from '@/lib/utils/convex-error-handler';

export interface ToastProps {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const TOAST_TITLE_MAX_LENGTH = 28;
const TOAST_DESCRIPTION_MAX_LENGTH = 56;

export function shortenToastText(
  value?: string,
  maxLength = TOAST_DESCRIPTION_MAX_LENGTH,
) {
  if (!value) return value;

  const replacements: Record<string, string> = {
    'Copied to Clipboard': 'Copied',
    'Validation Error': 'Check details',
    'Authentication Required': 'Sign in',
    'Sign In Required': 'Sign in',
    'Session Expired': 'Session expired',
    'Verification Successful': 'Verified',
    'Something went wrong. Please try again.': 'Please try again',
    'The link has been copied to your clipboard': 'Link copied',
  };

  const normalized = (replacements[value] || value).replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;

  const trimmed = normalized.slice(0, maxLength - 3);
  const wordBoundary = trimmed.lastIndexOf(' ');
  return `${trimmed.slice(0, wordBoundary > 18 ? wordBoundary : trimmed.length).trim()}...`;
}

export function useToast() {
  const toast = (props: ToastProps) => {
    const {
      title,
      description,
      variant = 'default',
      duration = 3000,
      action,
    } = props;
    const safeTitle = title
      ? shortenToastText(
          variant === 'destructive' ? getConvexErrorMessage(title, title) : title,
          TOAST_TITLE_MAX_LENGTH,
        )
      : title;
    const safeDescription = description
      ? shortenToastText(
          variant === 'destructive'
            ? getConvexErrorMessage(description)
            : description,
        )
      : description;
    
    switch (variant) {
      case 'destructive':
        sonnerToast.error(safeTitle || 'Error', {
          description: safeDescription,
          duration,
          action,
        });
        break;
      case 'success':
        sonnerToast.success(safeTitle || 'Success', {
          description: safeDescription,
          duration,
          action,
        });
        break;
      default:
        sonnerToast(safeTitle || 'Notification', {
          description: safeDescription,
          duration,
          action,
        });
        break;
    }
  };

  return { toast };
}
